import { HiddenMessage, MessageResponse, MessageType } from "@/generated-api";
import TextChatBubble from "./TextChatBubble";
import PhotosSharedChatBubble from "./PhotosSharedChatBubble";
import { useEffect, useState } from "react";
import { useMarkMessageRead } from "@/tanstack-hooks";
import { useInView } from "react-intersection-observer";
import PhotosRequestedChatBubble from "./PhotosRequestedChatBubble";
import ContactChatBubble from "./ContactChatBubble";
import HiddenChatBubble from "./HiddenChatBubble";

export default function ChatBubble({
  counterpartyId,
  message,
  scrollViewportRef,
  isInitialLoad,
}: {
  counterpartyId: string;
  message: MessageResponse;
  scrollViewportRef: React.RefObject<HTMLDivElement>;
  isInitialLoad: boolean;
}) {
  const [shouldObserve, setShouldObserve] = useState(!isInitialLoad);
  const markRead = useMarkMessageRead();
  const counterparty = message.sender_id === counterpartyId;

  const { ref } = useInView({
    threshold: 0.5,
    triggerOnce: true,
    root: scrollViewportRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]"
    ),
    skip: !shouldObserve,
    onChange: (inView) => {
      if (
        inView &&
        message.read_at === null &&
        counterparty &&
        message.message_type !== HiddenMessage.HIDDEN
      ) {
        markRead.mutate(message.message_id);
      }
    },
  });

  useEffect(() => {
    if (!isInitialLoad && !shouldObserve) {
      const timer = setTimeout(() => {
        setShouldObserve(true);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isInitialLoad, shouldObserve]);

  return (
    <div
      className={`w-full flex ${
        counterparty ? "justify-start" : "justify-end"
      }`}
      ref={ref}
    >
      {message.message_type === HiddenMessage.HIDDEN ? (
        <HiddenChatBubble counterpartyId={counterpartyId} message={message} />
      ) : message.message_type === MessageType.PHOTO_SHARE ? (
        <PhotosSharedChatBubble
          counterpartyId={counterpartyId}
          message={message}
        />
      ) : message.message_type === MessageType.PHOTO_REQUEST ? (
        <PhotosRequestedChatBubble
          counterpartyId={counterpartyId}
          message={message}
        />
      ) : message.message_type === MessageType.TEXT ? (
        <TextChatBubble counterpartyId={counterpartyId} message={message} />
      ) : (
        <ContactChatBubble counterpartyId={counterpartyId} message={message} />
      )}
    </div>
  );
}
